import { WeightUnit } from "@redotech/crdb-enums/product/weight-unit";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { CustomerTagSource } from "@redotech/redo-common-model-tags-common/customer-tag-record";
import {
  FulfillmentGroupTagSource,
  Tag,
  TagKind,
} from "@redotech/redo-common-model-tags-common/tag";
import { TimeUnit } from "@redotech/redo-common-model/time";
import {
  AddCustomerTagsActionStep,
  AddOrderTagsActionStep,
  AddTagActionStep,
  AssignFulfillmentServiceActionStep,
  AutoHoldType,
  DelayMarketplaceFulfillmentActionStep,
  GeneratePersonalizedSlipActionStep,
  OmsActionType,
  OmsSettableFields,
  PlaceHoldActionStep,
  PlaceHoldUntilActionStep,
  RemoveShipmentTagsActionStep,
  RoundWeightActionStep,
  SetAssignedUserActionStep,
  SetBuyReturnLabelActionStep,
  SetDeliveryMethodActionStep,
  SetDutiesPayerActionStep,
  SetInternalNoteActionStep,
  SetOriginLocationActionStep,
  SetOverrideUseOriginalDestinationAddressActionStep,
  SetPackingSlipTemplateActionStep,
  SetParcelActionStep,
  SetReturnAddressActionStep,
  SetSendLabelPurchasedNotificationActionStep,
  SetShippingOptionsActionStep,
  SetShippingServiceActionStep,
  SetShippingServiceCheapestActionStep,
  SetShippingServiceCheapestWithinDeliveryDaysActionStep,
  SetShippingServiceFastestActionStep,
  SetTaxIdentifiersActionStep,
  SetThirdPartyBillingAccountActionStep,
  SetWeightActionStep,
  UpdateShopifyOrderActionStep,
  UpdateShopifyOrderField,
  WeightRoundingType,
} from "@redotech/redo-flows-common/oms/actions";
import { OmsActionStep } from "@redotech/redo-flows-common/steps";
import {
  getPrettyCarrierName,
  getPrettyServiceName,
} from "@redotech/redo-fulfillment-carrier-common/carrier-service-labels";
import { Hazmat } from "@redotech/redo-fulfillment-carrier-common/hazmat";
import { RedoShopifyDeliveryMethodTypeToString } from "@redotech/redo-fulfillment-group-common/fulfillment-delivery-method-type";
import {
  type CheapestWithinDaysSortingCriteria,
  isCheapestWithinDaysSortingCriteria,
  SortingCriteria,
} from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { RedoFulfillmentLocation } from "@redotech/redo-fulfillment-group-common/fulfillment-location";
import { WeightFormat } from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import {
  splitWeight,
  Weight,
} from "@redotech/redo-fulfillment-outbound-labels-common/util";
import { T, t, useTranslation } from "@redotech/redo-i18n";
import PlayCircle from "@redotech/redo-icons/play-circle.svg";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { TagList } from "@redotech/redo-merchant-fulfillment-app/common/tag-list";
import { PillTheme } from "@redotech/redo-storefront-common/pill-theme";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { InfoTooltip } from "@redotech/redo-web/info-tooltip";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { Handle, Position } from "@xyflow/react";
import { useMemo } from "react";
import { PartialWithRequiredType } from "../../steps";
import { BaseNode } from "../base-node";
import * as NodeCSS from "../node.module.css";

export interface OmsActionNodeData {
  readonly: boolean;
  step: PartialWithRequiredType<OmsActionStep>;
  deleteElements: (id: string) => void;
}

function SetShippingServiceDetails({
  step,
}: {
  step: PartialWithRequiredType<SetShippingServiceActionStep>;
}) {
  const selectedServiceText =
    step.newShippingService && step.newShippingCarrier
      ? `${getPrettyCarrierName(step.newShippingCarrier)} ${getPrettyServiceName(step.newShippingService)}`
      : undefined;
  return (
    <T>
      Set <Text fontWeight="semibold">Shipping service</Text> to{" "}
      <Text fontWeight="semibold">{`${selectedServiceText}`}</Text>
    </T>
  );
}

function getDeliveryWindowText(
  sortingCriteria: CheapestWithinDaysSortingCriteria,
  count: number,
): string {
  switch (sortingCriteria) {
    case SortingCriteria.CHEAPEST_WITHIN_DELIVERY_DAYS:
      return t("within {{count}} delivery days", { count });
    case SortingCriteria.CHEAPEST_WITHIN_BUSINESS_DAYS:
      return t("within {{count}} business days", { count });
    case SortingCriteria.CHEAPEST_WITHIN_DELIVERABLE_DAYS:
      return t("within {{count}} deliverable days", { count });
    default:
      return assertNever(sortingCriteria);
  }
}

function SetShippingServiceGroupDetails({
  step,
}: {
  step:
    | PartialWithRequiredType<SetShippingServiceCheapestActionStep>
    | PartialWithRequiredType<SetShippingServiceFastestActionStep>
    | PartialWithRequiredType<SetShippingServiceCheapestWithinDeliveryDaysActionStep>;
}) {
  const { t } = useTranslation();
  const numServices = step.servicesToChooseFrom?.length;
  const isFastest =
    step.actionType === OmsActionType.SET_SHIPPING_SERVICE_FASTEST;
  let deliveryDaysText: string | undefined;

  if (
    step.actionType ===
      OmsActionType.SET_SHIPPING_SERVICE_CHEAPEST_WITHIN_DELIVERY_DAYS &&
    step.targetDeliveryDays
  ) {
    const sortingCriteria = isCheapestWithinDaysSortingCriteria(
      step.selectionCriteria,
    )
      ? step.selectionCriteria
      : SortingCriteria.CHEAPEST_WITHIN_DELIVERY_DAYS;
    deliveryDaysText = ` ${getDeliveryWindowText(sortingCriteria, step.targetDeliveryDays)}`;
  }

  return (
    <>
      <Text>
        {numServices
          ? isFastest
            ? t("Set shipping service to the fastest of {{count}} services", {
                count: numServices,
              })
            : t("Set shipping service to the cheapest of {{count}} services", {
                count: numServices,
              })
          : isFastest
            ? t("Set shipping service to the fastest of")
            : t("Set shipping service to the cheapest of")}
      </Text>
      {deliveryDaysText && (
        <Text fontWeight="semibold">{deliveryDaysText}</Text>
      )}
    </>
  );
}

function SetParcelDetails({
  step,
}: {
  step: PartialWithRequiredType<SetParcelActionStep>;
}) {
  const parcels = step.parcels?.length
    ? step.parcels
    : step.parcel
      ? [step.parcel]
      : [];

  const parcelNames = parcels.map((p) => p.name).join(", ");

  if (!parcelNames) {
    return (
      <T>
        Set <Text fontWeight="semibold">Parcel</Text> to{" "}
        <Text fontWeight="semibold">None</Text>
      </T>
    );
  }

  return parcels.length > 1 ? (
    <T>
      Set <Text fontWeight="semibold">Parcels</Text> to{" "}
      <Text fontWeight="semibold">{parcelNames}</Text>
    </T>
  ) : (
    <T>
      Set <Text fontWeight="semibold">Parcel</Text> to{" "}
      <Text fontWeight="semibold">{parcelNames}</Text>
    </T>
  );
}

function SetPackingSlipTemplateDetails({
  step,
}: {
  step: PartialWithRequiredType<SetPackingSlipTemplateActionStep>;
}) {
  return (
    <T>
      Set <Text fontWeight="semibold">Packing Slip</Text> to{" "}
      <Text fontWeight="semibold">{step.packingSlipName || ""}</Text>
    </T>
  );
}

function PersonalizedSlipDetails({
  step,
}: {
  step: PartialWithRequiredType<GeneratePersonalizedSlipActionStep>;
}) {
  return (
    <T>
      Generate <Text fontWeight="semibold">Personalized Insert</Text> using{" "}
      <Text fontWeight="semibold">
        {step.personalizedSlipTemplateName || ""}
      </Text>
    </T>
  );
}

function SetThirdPartyBillingAccountDetails({
  step,
}: {
  step: PartialWithRequiredType<SetThirdPartyBillingAccountActionStep>;
}) {
  return (
    <T>
      Set <Text fontWeight="semibold">billing account</Text> to{" "}
      <Text fontWeight="semibold">{step.thirdPartyBillingAccountName}</Text>
    </T>
  );
}

function SetOverrideUseOriginalDestinationAddressDetails({
  step,
}: {
  step: PartialWithRequiredType<SetOverrideUseOriginalDestinationAddressActionStep>;
}) {
  const { t } = useTranslation();
  return (
    <Flex dir="row" gap="xs">
      {step.overrideUseOriginalDestinationAddress ? (
        <>
          <Text fontWeight="semibold">
            {t("Use original, unverified destination address")}
          </Text>
          <InfoTooltip
            boxSize={16}
            tooltip={{
              title: t(
                "Use the original destination address instead of the verified address, even when the auto clean addresses setting is enabled",
              ),
            }}
          />
        </>
      ) : (
        <Text fontWeight="semibold">{t("Do nothing")}</Text>
      )}
    </Flex>
  );
}

function SetDeliveryMethodDetails({
  step,
}: {
  step: PartialWithRequiredType<SetDeliveryMethodActionStep>;
}) {
  return step.deliveryMethod ? (
    <T>
      Set <Text fontWeight="semibold">delivery method</Text> to{" "}
      <Text fontWeight="semibold">
        {RedoShopifyDeliveryMethodTypeToString[step.deliveryMethod]}
      </Text>
    </T>
  ) : (
    <T>
      Set <Text fontWeight="semibold">delivery method</Text> to{" "}
      <Text fontWeight="semibold">Not set</Text>
    </T>
  );
}

function AddTagDetails({
  step,
}: {
  step: PartialWithRequiredType<AddTagActionStep>;
}) {
  return (
    <>
      <T>
        Add <Text fontWeight="semibold">fulfillment tags</Text>
      </T>{" "}
      <Flex flex={1} pl="sm">
        <TagList tags={(step.tags || []) as Tag[]} />
      </Flex>
    </>
  );
}

function RemoveTagDetails({
  step,
}: {
  step: PartialWithRequiredType<RemoveShipmentTagsActionStep>;
}) {
  return (
    <>
      <T>
        Remove <Text fontWeight="semibold">fulfillment tags</Text>
      </T>{" "}
      <Flex flex={1} pl="sm">
        <TagList tags={(step.tags || []) as Tag[]} />
      </Flex>
    </>
  );
}

function AddCustomerTagsDetails({
  step,
}: {
  step: PartialWithRequiredType<AddCustomerTagsActionStep>;
}) {
  const tags: Tag[] = useMemo(
    () =>
      (step.tags ?? []).flatMap((tag) =>
        tag.name
          ? [
              {
                name: tag.name,
                pillTheme: PillTheme.NORMAL,
                source: CustomerTagSource.REDO_SUPPORT,
                kind: TagKind.CUSTOMER,
              },
            ]
          : [],
      ),
    [step.tags],
  );
  return (
    <>
      <T>
        Add <Text fontWeight="semibold">customer tags</Text>
      </T>{" "}
      <Flex flex={1} pl="sm">
        <TagList tags={tags} />
      </Flex>
    </>
  );
}

function AddOrderTagsDetails({
  step,
}: {
  step: PartialWithRequiredType<AddOrderTagsActionStep>;
}) {
  const tags: Tag[] = useMemo(
    () =>
      (step.tags ?? []).map((name) => ({
        name,
        pillTheme: PillTheme.NORMAL,
        source: FulfillmentGroupTagSource.MERCHANT,
        kind: TagKind.FULFILLMENT_GROUP,
      })),
    [step.tags],
  );
  return (
    <>
      <T>
        Add <Text fontWeight="semibold">order tags</Text>
      </T>{" "}
      <Flex flex={1} pl="sm">
        <TagList tags={tags} />
      </Flex>
    </>
  );
}

function SetShippingOptionsDetails({
  step,
}: {
  step: PartialWithRequiredType<SetShippingOptionsActionStep>;
}) {
  const { t } = useTranslation();
  const options = [];

  if (step.shippingOptions?.signatureRequired) {
    options.push(t("Signature required"));
  }

  if (step.shippingOptions?.adultSignatureRequired) {
    options.push(t("Adult signature required"));
  }

  if (step.shippingOptions?.includeInsurance) {
    options.push(t("Add additional insurance"));
  }

  if (step.shippingOptions?.overrideResidentialAddress) {
    options.push(t("Override residential address"));
  }

  if (step.shippingOptions?.hazmat === Hazmat.CLASS_9_UNMARKED_LITHIUM) {
    options.push(t("Shipment contains lithium batteries"));
  }

  if (step.shippingOptions?.hazmat === Hazmat.CLASS_9_DRY_ICE) {
    options.push(t("Shipment contains dry ice"));
  }

  return (
    <>
      <T>
        Set <Text fontWeight="semibold">Shipping Options</Text> to
      </T>{" "}
      <br />
      <ul>
        {options.length ? (
          options.map((option, index) => <li key={index}> - {option}</li>)
        ) : (
          <li>{t("None")}</li>
        )}
      </ul>
    </>
  );
}

function getHoldDurationText(timeUnit: TimeUnit, count: number): string {
  switch (timeUnit) {
    case TimeUnit.YEAR:
      return t("Place hold for {{count}} years", { count });
    case TimeUnit.MONTH:
      return t("Place hold for {{count}} months", { count });
    case TimeUnit.WEEK:
      return t("Place hold for {{count}} weeks", { count });
    case TimeUnit.DAY:
      return t("Place hold for {{count}} days", { count });
    case TimeUnit.HOUR:
      return t("Place hold for {{count}} hours", { count });
    case TimeUnit.MINUTE:
      return t("Place hold for {{count}} minutes", { count });
    case TimeUnit.SECOND:
      return t("Place hold for {{count}} seconds", { count });
    case TimeUnit.MILLISECOND:
      return t("Place hold for {{count}} milliseconds", { count });
    default:
      return assertNever(timeUnit);
  }
}

function PlaceHoldDetails({
  step,
}: {
  step: PartialWithRequiredType<PlaceHoldActionStep>;
}) {
  useTranslation();
  if (step.holdOptions?.holdType === AutoHoldType.Indefinite) {
    return (
      <T>
        Place hold<Text fontWeight="semibold"> indefinitely</Text>
      </T>
    );
  }

  const duration =
    step.holdOptions?.holdType === AutoHoldType.Duration &&
    step.holdOptions.duration
      ? step.holdOptions?.duration
      : 0;

  const timeUnit =
    step.holdOptions?.holdType === AutoHoldType.Duration &&
    step.holdOptions.timeUnit
      ? step.holdOptions?.timeUnit
      : TimeUnit.DAY;

  return <Text>{getHoldDurationText(timeUnit, duration)}</Text>;
}

function PlaceHoldUntilDetails({
  step,
}: {
  step: PartialWithRequiredType<PlaceHoldUntilActionStep>;
}) {
  const holdDate = step.holdOptions?.date;

  const formatDate = (date: Date) => {
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return holdDate instanceof Date ? (
    <T>
      Place hold until
      <Text fontWeight="semibold"> {formatDate(holdDate)} </Text>
    </T>
  ) : (
    <T>
      Place hold until<Text fontWeight="semibold"> N/A </Text>
    </T>
  );
}

function SetWeightDetails({
  step,
}: {
  step: PartialWithRequiredType<SetWeightActionStep>;
}) {
  const team = useRequiredContext(TeamContext);

  if (!step.weight?.unit || !step.weight?.value) {
    return (
      <T>
        Set weight to<Text fontWeight="semibold"> 0 </Text>
      </T>
    );
  }

  const useMetric =
    team.settings?.outboundLabels?.weightFormat === WeightFormat.METRIC;

  let weightText: string;

  if (useMetric) {
    const [kg, g] = splitWeight(step.weight as Weight, [
      WeightUnit.Kg,
      WeightUnit.G,
    ]);

    const parts = [];
    if (kg) {
      parts.push(`${kg} kg`);
    }

    if (g) {
      parts.push(`${g} g`);
    }

    weightText = parts.join(", ");
  } else {
    const [lbs, oz] = splitWeight(step.weight as Weight, [
      WeightUnit.Lb,
      WeightUnit.Oz,
    ]);

    const parts = [];
    if (lbs === 1) {
      parts.push(`${lbs} lb`);
    } else if (lbs > 1) {
      parts.push(`${lbs} lbs`);
    }

    if (oz) {
      parts.push(`${oz} oz`);
    }

    weightText = parts.join(", ");
  }

  return (
    <T>
      Set weight to<Text fontWeight="semibold"> {weightText} </Text>
    </T>
  );
}

function RoundWeightSummary({
  roundingType,
  weightText,
}: {
  roundingType: WeightRoundingType | undefined;
  weightText: string;
}) {
  switch (roundingType) {
    case WeightRoundingType.ROUND_UP:
      return (
        <T>
          <Text fontWeight="semibold">Round weight up</Text> when ≥{" "}
          <Text fontWeight="semibold">{weightText}</Text>
        </T>
      );
    case WeightRoundingType.ROUND_DOWN:
      return (
        <T>
          <Text fontWeight="semibold">Round weight down</Text> when ≥{" "}
          <Text fontWeight="semibold">{weightText}</Text>
        </T>
      );
    case undefined:
      return (
        <T>
          <Text fontWeight="semibold">Round weight unset</Text> when ≥{" "}
          <Text fontWeight="semibold">{weightText}</Text>
        </T>
      );
  }
}

function RoundWeightDetails({
  step,
}: {
  step: PartialWithRequiredType<RoundWeightActionStep>;
}) {
  const team = useRequiredContext(TeamContext);

  if (!step.minimumWeight?.unit || !step.minimumWeight?.value) {
    return (
      <RoundWeightSummary roundingType={step.roundingType} weightText="1 lb" />
    );
  }

  const useMetric =
    team.settings?.outboundLabels?.weightFormat === WeightFormat.METRIC;

  let weightText: string;

  if (useMetric) {
    const [kg, g] = splitWeight(step.minimumWeight as Weight, [
      WeightUnit.Kg,
      WeightUnit.G,
    ]);

    const parts = [];
    if (kg) {
      parts.push(`${kg} kg`);
    }

    if (g) {
      parts.push(`${g} g`);
    }

    weightText = parts.join(", ");
  } else {
    const [lbs, oz] = splitWeight(step.minimumWeight as Weight, [
      WeightUnit.Lb,
      WeightUnit.Oz,
    ]);

    const parts = [];
    if (lbs === 1) {
      parts.push(`${lbs} lb`);
    } else if (lbs > 1) {
      parts.push(`${lbs} lbs`);
    }

    if (oz) {
      parts.push(`${oz} oz`);
    }

    weightText = parts.join(", ");
  }

  return (
    <RoundWeightSummary
      roundingType={step.roundingType}
      weightText={weightText}
    />
  );
}

function SetAssignedUserDetails({
  step,
}: {
  step: PartialWithRequiredType<SetAssignedUserActionStep>;
}) {
  return (
    <T>
      Set <Text fontWeight="semibold">assigned user</Text>
    </T>
  );
}

function SetDutiesPayerDetails({
  step,
}: {
  step: PartialWithRequiredType<SetDutiesPayerActionStep>;
}) {
  return (
    <>
      {step.ddp == null ? (
        <T>
          Set <Text fontWeight="semibold">duties payer</Text>
        </T>
      ) : step.ddp ? (
        <T>
          Set <Text fontWeight="semibold">duties payer</Text>
          <Text fontWeight="semibold"> to shipper paid </Text>
        </T>
      ) : (
        <T>
          Set <Text fontWeight="semibold">duties payer</Text>
          <Text fontWeight="semibold"> to customer paid </Text>
        </T>
      )}
    </>
  );
}

function SetTaxIdentifiersDetails({
  step,
}: {
  step: PartialWithRequiredType<SetTaxIdentifiersActionStep>;
}) {
  return (
    <T>
      Set <Text fontWeight="semibold">tax identifiers</Text>
    </T>
  );
}

function SetReturnAddressDetails({
  step,
}: {
  step: PartialWithRequiredType<SetReturnAddressActionStep>;
}) {
  return (
    <T>
      Set <Text fontWeight="semibold">return address</Text> to{" "}
      <Text fontWeight="semibold">{step.address?.name}</Text>
    </T>
  );
}

function SetOriginLocationDetails({
  step,
}: {
  step: PartialWithRequiredType<SetOriginLocationActionStep>;
}) {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  const locationsLoad = useLoad(async () => {
    try {
      const { locations } = await rpcClient.getLocations(null);
      return locations;
    } catch (error) {
      console.error(error);
      return [];
    }
  }, []);

  const locationsMap = useMemo(() => {
    const map = new Map<string, RedoFulfillmentLocation>();
    if (locationsLoad.value) {
      for (const location of locationsLoad.value) {
        map.set(location.externalId, location);
      }
    }
    return map;
  }, [locationsLoad.value]);

  const locationName = step.locationId
    ? locationsMap.get(step.locationId)?.name
    : undefined;

  return (
    <>
      {step.locationId ? (
        <T>
          Set <Text fontWeight="semibold">fulfillment location</Text> to{" "}
          <Text fontWeight="semibold">{locationName || step.locationId}</Text>
        </T>
      ) : (
        <T>
          Set <Text fontWeight="semibold">fulfillment location</Text>
        </T>
      )}
    </>
  );
}

function SetFulfillmentServiceDetails({
  step,
}: {
  step: PartialWithRequiredType<AssignFulfillmentServiceActionStep>;
}) {
  return (
    <T>
      Set <Text fontWeight="semibold">fulfillment service</Text> to{" "}
      <Text fontWeight="semibold">{step.fulfillmentServiceName}</Text>
    </T>
  );
}

function SetBuyReturnLabelDetails({
  step,
}: {
  step: PartialWithRequiredType<SetBuyReturnLabelActionStep>;
}) {
  return (
    <>
      {step.buyReturnLabel ? (
        <T>
          Set <Text fontWeight="semibold">return label preference</Text> to{" "}
          <Text fontWeight="semibold">enabled</Text>
        </T>
      ) : (
        <T>
          Set <Text fontWeight="semibold">return label preference</Text> to{" "}
          <Text fontWeight="semibold">disabled</Text>
        </T>
      )}
    </>
  );
}
function SetSendLabelPurchasedNotificationDetails({
  step,
}: {
  step: PartialWithRequiredType<SetSendLabelPurchasedNotificationActionStep>;
}) {
  return (
    <>
      {step.sendLabelPurchasedNotification ? (
        <T>
          Set <Text fontWeight="semibold">label purchased notification</Text> to{" "}
          <Text fontWeight="semibold">enabled</Text>
        </T>
      ) : (
        <T>
          Set <Text fontWeight="semibold">label purchased notification</Text> to{" "}
          <Text fontWeight="semibold">disabled</Text>
        </T>
      )}
    </>
  );
}

function DelayMarketplaceFulfillmentDetails({
  step,
}: {
  step: PartialWithRequiredType<DelayMarketplaceFulfillmentActionStep>;
}) {
  const { t } = useTranslation();
  return (
    <Text>
      {t("Delay marketplace fulfillment by {{count}} days", {
        count: step.delayDays ?? 0,
      })}
    </Text>
  );
}
function UpdateShopifyOrderDetails({
  step,
}: {
  step: PartialWithRequiredType<UpdateShopifyOrderActionStep>;
}) {
  const { t } = useTranslation();
  return (
    <>
      {step.updateField === UpdateShopifyOrderField.SHIPPING_LABEL_COST
        ? t("Update {{platformName}} order shipping label cost", {
            platformName: "Shopify",
          })
        : t("Update {{platformName}} order ...", { platformName: "Shopify" })}
    </>
  );
}
function SetInternalNoteDetails({
  step,
}: {
  step: PartialWithRequiredType<SetInternalNoteActionStep>;
}) {
  return (
    <>
      {step.note ? (
        <T>
          Set <Text fontWeight="semibold">internal note</Text> to{" "}
          <Text fontWeight="semibold">
            {step.note.length > 50
              ? `${step.note.substring(0, 50)}...`
              : step.note}
          </Text>
        </T>
      ) : (
        <T>
          Set <Text fontWeight="semibold">internal note</Text>
        </T>
      )}
    </>
  );
}

// search-tag:ADDING-AN-OMS-ACTION
export function OmsActionNode(data: {
  id: string;
  data: OmsActionNodeData;
  selected?: boolean | undefined;
}) {
  const { t } = useTranslation();
  let onDelete: (() => void) | undefined;

  if (!data.data.readonly) {
    onDelete = () => data.data.deleteElements(data.id);
  }

  let details: React.ReactNode = null;
  switch (data.data.step.field) {
    case OmsSettableFields.SHIPPING_SERVICE:
      details = <SetShippingServiceDetails step={data.data.step} />;
      break;
    case OmsSettableFields.PARCEL:
      details = <SetParcelDetails step={data.data.step} />;
      break;
    case OmsSettableFields.SHIPPING_SERVICE_CHEAPEST:
      details = <SetShippingServiceGroupDetails step={data.data.step} />;
      break;
    case OmsSettableFields.SHIPPING_SERVICE_FASTEST:
      details = <SetShippingServiceGroupDetails step={data.data.step} />;
      break;
    case OmsSettableFields.SHIPPING_SERVICE_CHEAPEST_WITHIN_DELIVERY_DAYS:
      details = <SetShippingServiceGroupDetails step={data.data.step} />;
      break;
    case OmsSettableFields.PACKING_SLIP_TEMPLATE:
      details = <SetPackingSlipTemplateDetails step={data.data.step} />;
      break;
    case OmsSettableFields.TAG:
      details = <AddTagDetails step={data.data.step} />;
      break;
    case OmsSettableFields.REMOVE_SHIPMENT_TAGS:
      details = <RemoveTagDetails step={data.data.step} />;
      break;
    case OmsSettableFields.SHIPPING_OPTIONS:
      details = <SetShippingOptionsDetails step={data.data.step} />;
      break;
    case OmsSettableFields.PLACE_HOLD:
      details = <PlaceHoldDetails step={data.data.step} />;
      break;
    case OmsSettableFields.PLACE_HOLD_UNTIL:
      details = <PlaceHoldUntilDetails step={data.data.step} />;
      break;
    case OmsSettableFields.SET_WEIGHT:
      details = <SetWeightDetails step={data.data.step} />;
      break;
    case OmsSettableFields.ROUND_WEIGHT:
      details = <RoundWeightDetails step={data.data.step} />;
      break;
    case OmsSettableFields.ASSIGNED_USER:
      details = <SetAssignedUserDetails step={data.data.step} />;
      break;
    case OmsSettableFields.DUTIES_PAYER:
      details = <SetDutiesPayerDetails step={data.data.step} />;
      break;
    case OmsSettableFields.TAX_IDENTIFIERS:
      details = <SetTaxIdentifiersDetails step={data.data.step} />;
      break;
    case OmsSettableFields.RETURN_ADDRESS:
      details = <SetReturnAddressDetails step={data.data.step} />;
      break;
    case OmsSettableFields.FULFILLMENT_SERVICE:
      details = <SetFulfillmentServiceDetails step={data.data.step} />;
      break;
    case OmsSettableFields.ORIGIN_LOCATION:
      details = <SetOriginLocationDetails step={data.data.step} />;
      break;
    case OmsSettableFields.BUY_RETURN_LABEL:
      details = <SetBuyReturnLabelDetails step={data.data.step} />;
      break;
    case OmsSettableFields.THIRD_PARTY_BILLING_ACCOUNT:
      details = <SetThirdPartyBillingAccountDetails step={data.data.step} />;
      break;
    case OmsSettableFields.DELIVERY_METHOD:
      details = <SetDeliveryMethodDetails step={data.data.step} />;
      break;
    case OmsSettableFields.OVERRIDE_USE_ORIGINAL_DESTINATION_ADDRESS:
      details = (
        <SetOverrideUseOriginalDestinationAddressDetails
          step={data.data.step}
        />
      );
      break;
    case OmsSettableFields.PERSONALIZED_SLIP:
      details = <PersonalizedSlipDetails step={data.data.step} />;
      break;
    case OmsSettableFields.INTERNAL_NOTE:
      details = <SetInternalNoteDetails step={data.data.step} />;
      break;
    case OmsSettableFields.SPLIT_INTO_PARCELS:
      details = <Text>{t("Split items into own parcels")}</Text>;
      break;
    case OmsSettableFields.ORDER_TAG:
      details = <AddOrderTagsDetails step={data.data.step} />;
      break;
    case OmsSettableFields.CUSTOMER_TAG:
      details = <AddCustomerTagsDetails step={data.data.step} />;
      break;
    case OmsSettableFields.SEND_LABEL_PURCHASED_NOTIFICATION:
      details = (
        <SetSendLabelPurchasedNotificationDetails step={data.data.step} />
      );
      break;
    case OmsSettableFields.DELAY_MARKETPLACE_FULFILLMENT:
      details = <DelayMarketplaceFulfillmentDetails step={data.data.step} />;
      break;
    case OmsSettableFields.DO_NOT_NOTIFY_MARKETPLACE:
      details = <Text>{t("Do not notify marketplace")}</Text>;
      break;
    case OmsSettableFields.UPDATE_SHOPIFY_ORDER:
      details = <UpdateShopifyOrderDetails step={data.data.step} />;
      break;
    case undefined:
      details = <Text textColor="disabled">{t("Do nothing")}</Text>;
      break;
    default:
      assertNever(data.data.step);
  }
  return (
    <>
      <Handle position={Position.Top} type="target" />
      <BaseNode
        icon={<RedoFeaturedIcon color="success" Icon={PlayCircle} size="xs" />}
        onDelete={onDelete}
        selected={data.selected}
        step={data.data.step}
        title={t("Action")}
      >
        <Flex
          className={NodeCSS.fullInput}
          fontSize="xs"
          gap="none"
          wrap="wrap"
        >
          {details}
        </Flex>
      </BaseNode>
      <Handle position={Position.Bottom} type="source" />
    </>
  );
}
